/**
 * Struggling-learner flags from the Texas Tech / AAVMC-adapted formula spec.
 * Metrics write individual reason codes and are never collapsed into one risk score.
 */

export const STRUGGLING_REASON = {
  LOW_DOMAIN: "LOW_DOMAIN",
  BELOW_COHORT: "BELOW_COHORT",
  REPEAT_LOW: "REPEAT_LOW",
  NO_GROWTH: "NO_GROWTH",
  SUBCOMP_CLUSTER: "SUBCOMP_CLUSTER",
  FLAT_OR_FALLING: "FLAT_OR_FALLING",
  THIN_DATA: "THIN_DATA",
} as const;

export type StrugglingReasonCode =
  (typeof STRUGGLING_REASON)[keyof typeof STRUGGLING_REASON];

export type ObservationTiming = "mid" | "end";

export type ScoreObservation = {
  studentId: string;
  cohortId: string;
  rotationNumber: number;
  timing?: ObservationTiming | null;
  domainId: string;
  domainName: string;
  subcompetencyId: string;
  subcompetencyName: string;
  score: number;
};

export type StrugglingFlag = {
  studentId: string;
  cohortId: string;
  rotationNumber: number | null;
  domainId: string | null;
  domainName: string | null;
  reasonCode: StrugglingReasonCode;
  value: number | null;
  threshold: number | null;
  nObservations: number;
  escalated: boolean;
  details?: string;
};

export type LearnerStrugglingSummary = {
  studentId: string;
  isStruggling: boolean;
  hasThinData: boolean;
  escalated: boolean;
  flags: StrugglingFlag[];
  primaryReason: string;
};

const MIN_DOMAIN_SUBSCORES = 3;
const MIN_COHORT_CELL = 8;
const MIN_SD = 0.3;
const Z_FLAG = -1.5;
const MIN_ROTATION_SCORES_FOR_CLUSTER = 10;
const MIN_ROTATIONS_FOR_SLOPE = 3;
const MIN_ROTATIONS_FOR_COVERAGE = 3;
const COVERAGE_PCT = 0.7;
const DOMAIN_MISSING_SHARE = 0.5;

export const risingThreshold = (rotationNumber: number): number => {
  if (rotationNumber <= 3) return 2.5;
  if (rotationNumber <= 7) return 3.0;
  return 3.5;
};

const mean = (values: number[]) =>
  values.reduce((sum, value) => sum + value, 0) / values.length;

const stdDev = (values: number[]) => {
  if (values.length < 2) return 0;
  const avg = mean(values);
  const variance =
    values.reduce((sum, value) => sum + (value - avg) ** 2, 0) /
    (values.length - 1);
  return Math.sqrt(variance);
};

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

const olsSlope = (xs: number[], ys: number[]) => {
  const xbar = mean(xs);
  const ybar = mean(ys);
  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < xs.length; i += 1) {
    const dx = xs[i] - xbar;
    numerator += dx * (ys[i] - ybar);
    denominator += dx * dx;
  }
  if (denominator === 0) return 0;
  return numerator / denominator;
};

const domainScore = (rows: ScoreObservation[]) => {
  if (rows.length === 0) return null;
  return mean(rows.map((row) => row.score));
};

const byKey = <T,>(items: T[], keyFn: (item: T) => string) => {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const list = map.get(key) || [];
    list.push(item);
    map.set(key, list);
  }
  return map;
};

const flag = (
  partial: Omit<StrugglingFlag, "escalated"> & { escalated?: boolean },
): StrugglingFlag => ({
  escalated: false,
  ...partial,
});

export const evaluateStrugglingLearnerFlags = (
  observations: ScoreObservation[],
): StrugglingFlag[] => {
  const scored = observations.filter(
    (row) => Number.isFinite(row.score) && row.score > 0,
  );
  if (scored.length === 0) return [];

  const flags: StrugglingFlag[] = [];
  const metric1ByStudentDomain = new Map<string, Set<number>>();
  const metric2ByStudentDomain = new Map<string, Set<number>>();

  const markLow = (
    bucket: Map<string, Set<number>>,
    studentId: string,
    domainId: string,
    rotation: number,
  ) => {
    const key = `${studentId}::${domainId}`;
    const set = bucket.get(key) || new Set<number>();
    set.add(rotation);
    bucket.set(key, set);
  };

  const rotationDomainCells = byKey(
    scored,
    (row) => `${row.cohortId}::${row.rotationNumber}::${row.domainId}`,
  );

  for (const [cellKey, rows] of rotationDomainCells) {
    const [, rotationRaw, domainId] = cellKey.split("::");
    const rotationNumber = Number(rotationRaw);
    const threshold = risingThreshold(rotationNumber);
    const domainName = rows[0].domainName;
    const cohortId = rows[0].cohortId;

    const byStudent = byKey(rows, (row) => row.studentId);
    const studentScores: { studentId: string; score: number; n: number }[] = [];

    for (const [studentId, studentRows] of byStudent) {
      const uniqueSubs = new Set(studentRows.map((row) => row.subcompetencyId));
      const score = domainScore(studentRows);
      if (score == null) continue;

      const hasScoreOfOne = studentRows.some((row) => row.score === 1);
      if (hasScoreOfOne) {
        flags.push(
          flag({
            studentId,
            cohortId,
            rotationNumber,
            domainId,
            domainName,
            reasonCode: STRUGGLING_REASON.LOW_DOMAIN,
            value: score,
            threshold,
            nObservations: uniqueSubs.size,
            details: "Subcompetency score of 1",
          }),
        );
        markLow(metric1ByStudentDomain, studentId, domainId, rotationNumber);
      } else if (uniqueSubs.size >= MIN_DOMAIN_SUBSCORES && score < threshold) {
        flags.push(
          flag({
            studentId,
            cohortId,
            rotationNumber,
            domainId,
            domainName,
            reasonCode: STRUGGLING_REASON.LOW_DOMAIN,
            value: Number(score.toFixed(2)),
            threshold,
            nObservations: uniqueSubs.size,
          }),
        );
        markLow(metric1ByStudentDomain, studentId, domainId, rotationNumber);
      }

      studentScores.push({ studentId, score, n: uniqueSubs.size });
    }

    const cohortEligible = studentScores.filter(
      (row) => row.n >= MIN_DOMAIN_SUBSCORES,
    );
    if (cohortEligible.length >= MIN_COHORT_CELL) {
      const values = cohortEligible.map((row) => row.score);
      const cohortMean = mean(values);
      const sd = Math.max(stdDev(values), MIN_SD);
      for (const row of cohortEligible) {
        const z = (row.score - cohortMean) / sd;
        if (z <= Z_FLAG) {
          flags.push(
            flag({
              studentId: row.studentId,
              cohortId,
              rotationNumber,
              domainId,
              domainName,
              reasonCode: STRUGGLING_REASON.BELOW_COHORT,
              value: Number(z.toFixed(2)),
              threshold: Z_FLAG,
              nObservations: row.n,
            }),
          );
          markLow(
            metric2ByStudentDomain,
            row.studentId,
            domainId,
            rotationNumber,
          );
        }
      }
    }
  }

  const lowRotations = new Map<string, Set<number>>();
  for (const [key, rotations] of metric1ByStudentDomain) {
    lowRotations.set(key, new Set(rotations));
  }
  for (const [key, rotations] of metric2ByStudentDomain) {
    const set = lowRotations.get(key) || new Set<number>();
    rotations.forEach((rotation) => set.add(rotation));
    lowRotations.set(key, set);
  }

  for (const [key, rotations] of lowRotations) {
    const [studentId, domainId] = key.split("::");
    if (rotations.size < 2) continue;
    const ordered = [...rotations].sort((a, b) => a - b);
    const consecutive = ordered.some(
      (rotation, index) => index > 0 && rotation === ordered[index - 1] + 1,
    );
    const sample = scored.find(
      (row) => row.studentId === studentId && row.domainId === domainId,
    );
    flags.push(
      flag({
        studentId,
        cohortId: sample?.cohortId || "",
        rotationNumber: ordered[ordered.length - 1],
        domainId,
        domainName: sample?.domainName || domainId,
        reasonCode: STRUGGLING_REASON.REPEAT_LOW,
        value: ordered.length,
        threshold: 2,
        nObservations: ordered.length,
        escalated: consecutive,
        details: consecutive ? "Consecutive rotations" : undefined,
      }),
    );
  }

  const studentRotationDomain = byKey(
    scored,
    (row) =>
      `${row.studentId}::${row.cohortId}::${row.rotationNumber}::${row.domainId}`,
  );
  for (const [, rows] of studentRotationDomain) {
    const midRows = rows.filter((row) => row.timing === "mid");
    const endRows = rows.filter((row) => row.timing === "end");
    if (midRows.length === 0 || endRows.length === 0) continue;
    const midScore = domainScore(midRows);
    const endScore = domainScore(endRows);
    if (midScore == null || endScore == null) continue;
    const rotationNumber = rows[0].rotationNumber;
    const threshold = risingThreshold(rotationNumber);
    const delta = endScore - midScore;
    if (midScore < threshold && delta <= 0) {
      flags.push(
        flag({
          studentId: rows[0].studentId,
          cohortId: rows[0].cohortId,
          rotationNumber,
          domainId: rows[0].domainId,
          domainName: rows[0].domainName,
          reasonCode: STRUGGLING_REASON.NO_GROWTH,
          value: Number(delta.toFixed(2)),
          threshold,
          nObservations: midRows.length + endRows.length,
        }),
      );
    }
  }

  const studentRotations = byKey(
    scored,
    (row) => `${row.studentId}::${row.cohortId}::${row.rotationNumber}`,
  );
  for (const [, rows] of studentRotations) {
    if (rows.length < MIN_ROTATION_SCORES_FOR_CLUSTER) continue;
    const n1 = rows.filter((row) => row.score === 1).length;
    const n2 = rows.filter((row) => row.score <= 2).length;
    if (n1 >= 1 || n2 >= 3) {
      const lows = rows
        .filter((row) => row.score <= 2)
        .map((row) => row.subcompetencyName)
        .filter(Boolean);
      flags.push(
        flag({
          studentId: rows[0].studentId,
          cohortId: rows[0].cohortId,
          rotationNumber: rows[0].rotationNumber,
          domainId: null,
          domainName: null,
          reasonCode: STRUGGLING_REASON.SUBCOMP_CLUSTER,
          value: n2,
          threshold: n1 >= 1 ? 1 : 3,
          nObservations: rows.length,
          details: `N1=${n1}, N2=${n2}${lows.length ? `; ${lows.slice(0, 6).join(", ")}` : ""}`,
        }),
      );
    }
  }

  const studentDomain = byKey(
    scored,
    (row) => `${row.studentId}::${row.cohortId}::${row.domainId}`,
  );
  const cohortRotationDomainScores = byKey(
    scored,
    (row) => `${row.cohortId}::${row.rotationNumber}::${row.domainId}`,
  );

  for (const [, rows] of studentDomain) {
    const byRotation = byKey(rows, (row) => String(row.rotationNumber));
    const series = [...byRotation.entries()]
      .map(([rotation, rotationRows]) => ({
        rotation: Number(rotation),
        score: domainScore(rotationRows) || 0,
      }))
      .sort((a, b) => a.rotation - b.rotation);
    if (series.length < MIN_ROTATIONS_FOR_SLOPE) continue;

    const slope = olsSlope(
      series.map((row) => row.rotation),
      series.map((row) => row.score),
    );
    const latest = series[series.length - 1];
    const cohortRows =
      cohortRotationDomainScores.get(
        `${rows[0].cohortId}::${latest.rotation}::${rows[0].domainId}`,
      ) || [];
    const cohortByStudent = byKey(cohortRows, (row) => row.studentId);
    const cohortValues = [...cohortByStudent.values()]
      .map((studentRows) => domainScore(studentRows))
      .filter((value): value is number => value != null);
    if (cohortValues.length === 0) continue;
    const cohortMedian = median(cohortValues);
    if (slope <= 0 && latest.score < cohortMedian) {
      flags.push(
        flag({
          studentId: rows[0].studentId,
          cohortId: rows[0].cohortId,
          rotationNumber: latest.rotation,
          domainId: rows[0].domainId,
          domainName: rows[0].domainName,
          reasonCode: STRUGGLING_REASON.FLAT_OR_FALLING,
          value: Number(slope.toFixed(2)),
          threshold: cohortMedian,
          nObservations: series.length,
        }),
      );
    }
  }

  const byCohort = byKey(scored, (row) => row.cohortId);
  for (const [cohortId, cohortRows] of byCohort) {
    const students = new Set(cohortRows.map((row) => row.studentId));
    for (const studentId of students) {
      const studentRows = cohortRows.filter((row) => row.studentId === studentId);
      const rotationsCompleted = Math.max(
        ...studentRows.map((row) => row.rotationNumber),
      );
      const expectedRows = cohortRows.filter(
        (row) => row.rotationNumber <= rotationsCompleted,
      );
      const expectedSubs = new Set(expectedRows.map((row) => row.subcompetencyId));
      const scoredSubs = new Set(studentRows.map((row) => row.subcompetencyId));
      const expected = expectedSubs.size;
      const scoredCount = [...expectedSubs].filter((id) =>
        scoredSubs.has(id),
      ).length;
      if (expected === 0) continue;

      const pct = scoredCount / expected;
      const missing = [...expectedSubs].filter((id) => !scoredSubs.has(id));
      const expectedDomainBySub = new Map<string, string>();
      for (const row of expectedRows) {
        expectedDomainBySub.set(row.subcompetencyId, row.domainId);
      }
      const missingByDomain = new Map<string, number>();
      for (const id of missing) {
        const domainId = expectedDomainBySub.get(id) || "unknown";
        missingByDomain.set(domainId, (missingByDomain.get(domainId) || 0) + 1);
      }
      const topMissing = [...missingByDomain.entries()].sort(
        (a, b) => b[1] - a[1],
      )[0];
      const concentrated =
        missing.length > 0 &&
        !!topMissing &&
        topMissing[1] / missing.length > DOMAIN_MISSING_SHARE;

      if (
        (rotationsCompleted >= MIN_ROTATIONS_FOR_COVERAGE && pct < COVERAGE_PCT) ||
        concentrated
      ) {
        const domainName =
          expectedRows.find((row) => row.domainId === topMissing?.[0])
            ?.domainName || topMissing?.[0];
        flags.push(
          flag({
            studentId,
            cohortId,
            rotationNumber: rotationsCompleted,
            domainId: topMissing?.[0] || null,
            domainName: domainName || null,
            reasonCode: STRUGGLING_REASON.THIN_DATA,
            value: Number(pct.toFixed(2)),
            threshold: COVERAGE_PCT,
            nObservations: scoredCount,
            details: concentrated
              ? `Most missing scores in ${domainName}`
              : undefined,
          }),
        );
      }
    }
  }

  return flags;
};

const REASON_PRIORITY: StrugglingReasonCode[] = [
  STRUGGLING_REASON.LOW_DOMAIN,
  STRUGGLING_REASON.REPEAT_LOW,
  STRUGGLING_REASON.NO_GROWTH,
  STRUGGLING_REASON.BELOW_COHORT,
  STRUGGLING_REASON.SUBCOMP_CLUSTER,
  STRUGGLING_REASON.FLAT_OR_FALLING,
  STRUGGLING_REASON.THIN_DATA,
];

const reasonLabel: Record<StrugglingReasonCode, string> = {
  LOW_DOMAIN: "Low scores in an area",
  BELOW_COHORT: "Well below the cohort",
  REPEAT_LOW: "Repeated low scores",
  NO_GROWTH: "No improvement in a rotation",
  SUBCOMP_CLUSTER: "Cluster of low subcompetency scores",
  FLAT_OR_FALLING: "Flat or declining scores",
  THIN_DATA: "Not enough scores to assess",
};

export const describeStrugglingFlag = (flagRow: StrugglingFlag): string => {
  const area = flagRow.domainName || "this area";
  const rotation =
    flagRow.rotationNumber != null
      ? ` (rotation ${flagRow.rotationNumber})`
      : "";

  switch (flagRow.reasonCode) {
    case STRUGGLING_REASON.LOW_DOMAIN:
      if (flagRow.details) {
        return `${flagRow.details} in ${area}${rotation}`;
      }
      return (
        `Scores in ${area} are below the expected level${rotation}` +
        (flagRow.value != null && flagRow.threshold != null
          ? ` (mean ${flagRow.value}, expected at least ${flagRow.threshold})`
          : "")
      );
    case STRUGGLING_REASON.BELOW_COHORT:
      return (
        `Scoring 1.5 or more standard deviations below the cohort in ${area}${rotation}` +
        (flagRow.value != null ? ` (z = ${flagRow.value})` : "")
      );
    case STRUGGLING_REASON.REPEAT_LOW:
      return (
        `Low scores in ${area} across ${flagRow.value ?? "multiple"} rotations` +
        (flagRow.escalated ? "; consecutive rotations" : "")
      );
    case STRUGGLING_REASON.NO_GROWTH:
      return `No improvement in ${area} from mid to end of the rotation${rotation}`;
    case STRUGGLING_REASON.SUBCOMP_CLUSTER:
      return (
        `Several low subcompetency scores in the same rotation${rotation}` +
        (flagRow.details ? ` (${flagRow.details})` : "")
      );
    case STRUGGLING_REASON.FLAT_OR_FALLING:
      return `Scores in ${area} are flat or declining and below the cohort median${rotation}`;
    case STRUGGLING_REASON.THIN_DATA:
      return (
        `Not enough scores to assess${rotation}` +
        (flagRow.details ? ` — ${flagRow.details}` : "")
      );
    default:
      return reasonLabel[flagRow.reasonCode] || flagRow.reasonCode;
  }
};

export const formatStrugglingReason = (flagRow: StrugglingFlag): string =>
  describeStrugglingFlag(flagRow);

export const summarizeStrugglingLearners = (
  observations: ScoreObservation[],
): Map<string, LearnerStrugglingSummary> => {
  const flags = evaluateStrugglingLearnerFlags(observations);
  const byStudent = byKey(flags, (row) => row.studentId);
  const summaries = new Map<string, LearnerStrugglingSummary>();

  for (const [studentId, studentFlags] of byStudent) {
    const performanceFlags = studentFlags.filter(
      (row) => row.reasonCode !== STRUGGLING_REASON.THIN_DATA,
    );
    const thinFlags = studentFlags.filter(
      (row) => row.reasonCode === STRUGGLING_REASON.THIN_DATA,
    );
    const ordered = [...studentFlags].sort((a, b) => {
      if (a.escalated !== b.escalated) return a.escalated ? -1 : 1;
      return (
        REASON_PRIORITY.indexOf(a.reasonCode) -
        REASON_PRIORITY.indexOf(b.reasonCode)
      );
    });
    const primary = ordered[0];
    summaries.set(studentId, {
      studentId,
      isStruggling: performanceFlags.length > 0,
      hasThinData: thinFlags.length > 0,
      escalated: studentFlags.some((row) => row.escalated),
      flags: ordered,
      primaryReason: primary ? formatStrugglingReason(primary) : "",
    });
  }

  return summaries;
};
